import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Carousel } from 'bootstrap';
import './HorizontalTimeline.css';

const HorizontalTimeline = ({ section, isRightAligned }) => {
  const displayItems = useMemo(() => section?.items || [], [section?.items]);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const opacityValue = (section?.background_opacity || 100) / 100;

  // CHAVE DE CONTROLE DO LAYOUT
  // true = Alterna cima/baixo | false = Todos os cards ficam para baixo
  const alternarCards = false;

  const [selectedImage, setSelectedImage] = useState(null);

  // Variáveis de cabeçalho
  const alignClass = isRightAligned ? "section-header-right mb-5 pb-4" : "mb-5 text-center pb-4";
  const revealDirection = isRightAligned ? "reveal-from-left" : "reveal-from-bottom";

  // ==== ESTADOS E REFS PARA ROLAGEM HORIZONTAL ====
  const trackRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  // Lógica de arrastar com o mouse (Drag to Scroll)
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX - trackRef.current.offsetLeft);
    setScrollLeft(trackRef.current.scrollLeft);
  };
  const handleMouseLeave = () => setIsDragging(false);
  const handleMouseUp = () => setIsDragging(false);
  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - trackRef.current.offsetLeft;
    const walk = (x - startX) * 1.5; // Velocidade do arraste
    trackRef.current.scrollLeft = scrollLeft - walk;
  };

  // Lógica das setas: Avança/Recua o tamanho exato de 1 card
  const scrollByAmount = (amount) => {
    if (trackRef.current) {
      trackRef.current.scrollBy({ left: amount, behavior: 'smooth' });
    }
  };

  // ==== INICIALIZAÇÃO DOS CARROSSÉIS INTERNOS (MANTIDA) ====
  useEffect(() => {
    const timer = setTimeout(() => {
      if (displayItems.length > 0) {
        const carouselElements = document.querySelectorAll('.carousel-timeline-sync');
        carouselElements.forEach((el) => {
          const intervalTime = el.getAttribute('data-bs-interval') || 5000;
          const bsCarousel = new Carousel(el, {
            interval: parseInt(intervalTime, 10),
            ride: 'carousel',
            pause: 'hover'
          });
          bsCarousel.cycle();
        });
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [displayItems]);

  const dynamicStyle = {
    position: 'relative',
    overflow: 'hidden' 
  };

  if (section?.background_type === 'color' || (!section?.background_image_url && section?.background_color)) {
    dynamicStyle.backgroundColor = section.background_color;
  }

  if (!section) return null;

  return (
    <section 
      id={section.slug} 
      className={`py-5 timeline-section-h ${(!section.background_color && !section.background_image_url) ? 'default-section' : ''}`} 
      style={dynamicStyle}
    >
      {/* CAMADA 0: Imagem com bg-parallax */}
      {section.background_type === 'image' && section.background_image_url && (
        <div className="bg-parallax" style={{ 
          position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', 
          backgroundImage: `url(${backendUrl}${section.background_image_url})`,
          backgroundSize: 'cover', backgroundPosition: 'center', zIndex: 0 
        }}></div>
      )}

      {/* CAMADA 1: Overlay */}
      {section.background_type === 'image' && section.background_image_url && (
        <div style={{ 
          position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', 
          backgroundColor: 'rgba(255, 255, 255, 0.99)', 
          opacity: opacityValue, zIndex: 1 
        }}></div>
      )}

      {/* CAMADA 2: Container de Conteúdo */}
      <div style={{ position: 'relative', zIndex: 2 }}>
        
        {/* CABEÇALHO DA SEÇÃO */}
        <div className={`container pt-5 ${alignClass}`}>
          <h2 className={`fonte-primaria display-4 section-title reveal-element ${revealDirection} duration-10`}>
            {section.title}
          </h2>
          <h4 className={`fonte-terciaria section-subtitle mb-4 reveal-element ${revealDirection} duration-12`}>
            {section.subtitle}
          </h4>
          {section.content && (
            <p className={`fonte-terciaria lead text-dark reveal-element ${revealDirection} duration-14`}>
              {section.content}
            </p>
          )}
        </div>

        {/* TRILHA HORIZONTAL INFINITA */}
        <div className={`timeline-h-wrapper position-relative mx-auto ${alternarCards ? 'layout-alternate' : 'layout-bottom'}`} style={{ maxWidth: '1600px', padding: '0 60px' }}>
          
          <button className="timeline-arrow prev shadow-sm" onClick={() => scrollByAmount(-400)}>
            <i className="bi bi-chevron-left"></i>
          </button>

          <div 
            className={`timeline-h-track ${isDragging ? 'dragging' : ''}`}
            ref={trackRef}
            onMouseDown={handleMouseDown}
            onMouseLeave={handleMouseLeave}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
          >
            {displayItems.map((item, index) => {
              
              // ==========================================
              // A MÁGICA DA ALTERNÂNCIA AQUI
              // Se 'alternarCards' for true, ele faz o par/ímpar.
              // Se for false, ele força todos a serem false (ou seja, 'bottom')
              // ==========================================
              const isTop = alternarCards ? (index % 2 === 0) : false;
              const positionClass = isTop ? 'top' : 'bottom';
              
              let stateClass = '';
              if (item.progress_percentage > 0 && item.progress_percentage < 100) stateClass = 'phase-current'; 
              else if (item.progress_percentage === 0) stateClass = 'phase-future';  

              const carouselId = `carousel-timeline-${item.id}`;
              const randomInterval = Math.floor(Math.random() * (6500 - 4500 + 1)) + 4500;

              return (
                <div className={`timeline-h-item ${positionClass} ${stateClass}`} key={item.id}>
                  
                  <div className="timeline-h-dot"></div>
                  
                  {/* CONTEÚDO ORIGINAL DO SEU CARD MANTIDO AQUI */}
                  <div className="timeline-h-card fonte-terciaria shadow-sm border bg-white">
                    <div className="phase-badge mb-3">Fase {item.item_order}</div>
                    <h5 className="fw-bold item-title" style={{ color: 'var(--cor-primaria-5)' }}>{item.title}</h5>
                    <p className="text-muted small">{item.content || item.description}</p>
                    
                    {/* CARROSSEL INTERNO */}
                    {item.images && item.images.length > 0 && (
                      <div 
                        id={carouselId} 
                        className="carousel slide mt-3 mb-3 carousel-timeline-sync" 
                        data-bs-interval={randomInterval}
                      >
                        <div className="carousel-inner rounded shadow-sm">
                          {item.images.map((img, i) => (
                            <div key={img.id} className={`carousel-item ${i === 0 ? 'active' : ''}`}>
                              <img 
                                src={`${backendUrl}${img.image_url}`} 
                                className="d-block w-100 timeline-img-clickable" 
                                alt={`Foto ${i+1}`} 
                                draggable="false" 
                                style={{ height: '200px', objectFit: 'cover', cursor: 'pointer' }} /* <-- Cursor pointer */
                                onClick={() => setSelectedImage(img.image_url)} /* <-- Abre o modal com esta imagem */
                              />
                            </div>
                          ))}
                        </div>
                        {item.images.length > 1 && (
                          <>
                            <button className="carousel-control-prev" type="button" data-bs-target={`#${carouselId}`} data-bs-slide="prev">
                              <span className="carousel-control-prev-icon bg-dark rounded-circle p-2" aria-hidden="true"></span>
                            </button>
                            <button className="carousel-control-next" type="button" data-bs-target={`#${carouselId}`} data-bs-slide="next">
                              <span className="carousel-control-next-icon bg-dark rounded-circle p-2" aria-hidden="true"></span>
                            </button>
                          </>
                        )}
                      </div>
                    )}

                    {/* BARRA DE PROGRESSO */}
                    <div className="progress mt-3" style={{ height: '6px' }}>
                      <div 
                        className="progress-bar progress-bar-custom" 
                        role="progressbar" 
                        style={{ width: `${item.progress_percentage || 0}%`, backgroundColor: 'var(--cor-secundaria-3)' }} 
                      ></div>
                    </div>
                    <small className="text-muted d-block mt-1 fw-bold">{item.progress_percentage || 0}% Concluído</small>
                  </div>
                </div>
              );
            })}
          </div>

          <button className="timeline-arrow next shadow-sm" onClick={() => scrollByAmount(400)}>
            <i className="bi bi-chevron-right"></i>
          </button>

        </div>
      </div>

      {/* ========================================== */}
      {/* LIGHTBOX NATIVO PARA AMPLIAR IMAGEM        */}
      {/* ========================================== */}
      {selectedImage && (
        <div 
          className="timeline-lightbox-overlay" 
          onClick={() => setSelectedImage(null)} /* Clicar no fundo escuro fecha */
        >
          <button className="timeline-lightbox-close" onClick={() => setSelectedImage(null)}>
            <i className="bi bi-x-lg"></i>
          </button>
          <img 
            src={`${backendUrl}${selectedImage}`} 
            alt="Obra Ampliada" 
            className="timeline-lightbox-img shadow-lg" 
            onClick={(e) => e.stopPropagation()} /* Impede que clicar NA imagem feche o modal */
          />
        </div>
      )}
    </section>
  );
};

export default HorizontalTimeline;
